import { useCallback, useEffect, useMemo, useState } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import { useQuery } from '@zenbusiness/application-commons-graphql';
import { useError } from '@zenbusiness/application-commons-logging';
import { WebView } from '@zenbusiness/application-commons-webview';
import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';
import Tab from '@zenbusiness/zui/Tab';
import TabContext from '@zenbusiness/zui/TabContext';
import TabList from '@zenbusiness/zui/TabList';
import TabPanel from '@zenbusiness/zui/TabPanel';

import TransactionSkeleton from '#client/components/TransactionListSkeleton';
import { FINISH_CHECK_DEPOSIT } from '#client/constants/eventName';
import { FAQ_CHECK_HISTORY_URL } from '#client/constants/FAQs';
import { PAGINATION_DEFAULT_LIMIT } from '#client/constants/pagination';
import {
  RENDER_TRANSACTION_TYPE,
  TRANSACTION_DIRECTION,
  checkDepositStatusOptions
} from '#client/constants/transaction';
import useCheckDepositModal from '#client/hooks/useCheckDepositModal';
import useCheckOpenModal from '#client/hooks/useCheckOpenModals';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useDisableDashboardContext from '#client/hooks/useDisableDashboardContext';
import useEventListeners from '#client/hooks/useEventListeners';
import useMemoObject from '#client/hooks/useMemoObject';
import useSkeleton from '#client/hooks/useSkeleton';
import useToastContext from '#client/hooks/useToastContext';
import useUnitAccountId from '#client/hooks/useUnitAccountId';
import ListPendingSection from '#client/pages/TransactionHistory/components/ListPendingSection';
import {
  GET_LIST_CHECK_DEPOSIT_QUERY,
  GET_LIST_TRANSACTION,
  GET_PENDING_SECTION
} from '#client/queries';
import { getDateRange, getMonthSelectOptions } from '#client/utils/date';
import { checkIsMobile } from '#client/utils/devices';
import { formatTransaction } from '#client/utils/transaction';

import FilterCheckHistory from './components/FilterCheckHistory';
import FilterTransactions from './components/FilterTransactions';
import { DialogContentStyled, TabBoxStyled } from './TransactionHistory.styles';
import EmptyPayment from '../EmptyPayment';
import EmptyTransaction from '../EmptyTransaction';
import FilterOptions from '../FilterOptions';
import ListTransaction from '../ListTransaction';
import MobileDrawer from '../MobileDrawer';
import NoResultTransaction from '../NoResultTransaction';
import PageTitle from '../PageTitle';
import StatementsModal from '../StatementsModal';

import {
  initFilter,
  MONTH_CHECK_HISTORY,
  MONTH_TRANSACTIONS,
  STATUS,
  TYPE
} from './constants/filter';
import { getStatusPayload } from './utils/status';
import ListCheckDeposit from '../CheckDepositHistory/components/ListCheckDeposit';

const typeOptions = [
  { key: 'all', name: 'All' },
  { key: 'credit', name: 'Credits' },
  { key: 'debit', name: 'Debits' }
];

const TRANSACTIONS_TAB = '1';
const CHECKS_TAB = '2';

const monthOptions = getMonthSelectOptions();

export default function TransactionHistory() {
  const { isWebView } = WebView;
  const { isDisable: isDisableDashboard } = useDisableDashboardContext();

  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { unitAccountId, isValidMfa, isEnabled, isEnrolled, isFetchingMfaStatus } =
    useUnitAccountId();
  const { isOpeningModal } = useCheckOpenModal();
  const { createCommonErrorToast } = useToastContext();
  const openCheckDepositModal = useCheckDepositModal();

  const [filters, setFilters] = useState(initFilter);
  const [isOpenStatement, setIsOpenStatement] = useState(false);
  const [isClickLoadMore, setIsClickLoadMore] = useState(false);
  const [showMonthOptions, setShowMonthOptions] = useState(false);
  const [showMonthCheckHistoryOptions, setShowMonthCheckHistoryOptions] = useState(false);
  const [showTypeOptions, setShowTypeOptions] = useState(false);
  const [showStatusOptions, setShowStatusOptions] = useState(false);
  const [offset, setOffset] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [currentTab, setCurrentTab] = useState(TRANSACTIONS_TAB);
  const isSkipQuery =
    isFetchingMfaStatus ||
    !isEnrolled ||
    !isEnabled ||
    !isValidMfa ||
    !unitAccountId ||
    isOpeningModal;

  const updateOriginalTransactions = (prevData, data) => {
    const transactionList = isClickLoadMore
      ? [...prevData.transactions.data, ...data.transactions.data]
      : data.transactions.data;
    setIsClickLoadMore(false);
    return {
      transactions: {
        data: transactionList,
        pagination: data.transactions.pagination
      }
    };
  };

  const {
    loading: loadingTransaction,
    error: transactionError,
    data: originalData
  } = useQuery(GET_LIST_TRANSACTION, {
    variables: {
      params: {
        filter: {
          businessEntityUuid,
          search: searchValue ?? undefined,
          direction: filters[TYPE] === '' || filters[TYPE] === 'all' ? undefined : filters[TYPE],
          month:
            filters[MONTH_TRANSACTIONS] === '' || filters[MONTH_TRANSACTIONS] === 'all'
              ? undefined
              : getDateRange(filters[MONTH_TRANSACTIONS])
        },
        pagination: {
          limit: PAGINATION_DEFAULT_LIMIT,
          offset
        }
      }
    },
    noThrow: true,
    skipCache: true,
    skip: isSkipQuery,
    updateData: updateOriginalTransactions
  });

  useError(transactionError);
  const data = useMemoObject(originalData);

  const getPaymentType = (transactionType) =>
    transactionType === TRANSACTION_DIRECTION.DEBIT
      ? TRANSACTION_DIRECTION.CREDIT
      : TRANSACTION_DIRECTION.DEBIT;

  const {
    error: errorPendingSection,
    refetch: refetchPendingSection,
    loading: loadingPendingSection,
    data: originalPendingSectionData
  } = useQuery(GET_PENDING_SECTION, {
    variables: {
      params: {
        identifier: {
          businessUuid: businessEntityUuid,
          unitAccountId
        },
        filter: {
          direction:
            filters[TYPE] === '' || filters[TYPE] === 'all'
              ? undefined
              : getPaymentType(filters[TYPE]),
          month:
            filters[MONTH_TRANSACTIONS] === '' || filters[MONTH_TRANSACTIONS] === 'all'
              ? undefined
              : getDateRange(filters[MONTH_TRANSACTIONS])
        },
        pagination: {
          limit: PAGINATION_DEFAULT_LIMIT,
          offset: 0
        }
      }
    },
    noThrow: true,
    skipCache: true,
    skip: isSkipQuery
  });

  useError(errorPendingSection);
  const pendingSectionData = useMemoObject(originalPendingSectionData);

  useEffect(() => {
    const errors = transactionError || errorPendingSection;
    if (errors) {
      createCommonErrorToast({ error: errors });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionError, errorPendingSection]);

  const monthTransactions = useMemo(
    () => formatTransaction(data?.transactions?.data ?? []),
    [data]
  );

  const { items: pendingSectionList } = pendingSectionData?.pendingSection || {};

  const pendingSection = useMemo(() => {
    if (searchValue || !pendingSectionList) {
      return [];
    }

    const transform = (obj) => {
      if (!obj) {
        return null;
      }
      const { fullAccountNumber, accountNumber, ...rest } = obj;
      return {
        ...rest,
        accountNumberLast4: accountNumber,
        accountNumber: fullAccountNumber
      };
    };

    return pendingSectionList.map((item) => ({
      ...item,
      from: transform(item.from),
      to: transform(item.to)
    }));
  }, [pendingSectionList, searchValue]);

  const hasMore = useMemo(
    () => !!data?.transactions?.pagination?.next && monthTransactions.length > 0,
    [data?.transactions?.pagination?.next, monthTransactions.length]
  );

  // Base on the LD to hide skeleton
  const isHideSkeleton =
    (data && pendingSectionData && !loadingTransaction && !loadingPendingSection) ||
    transactionError;
  const [showSkeleton, setShowSkeleton] = useSkeleton(isHideSkeleton);

  const handleFilterCommon = useCallback(() => {
    setOffset(0);
    setShowSkeleton();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeFilters = (filter, value) => {
    setFilters((prev) => ({
      ...prev,
      [filter]: value
    }));
  };

  const handleDesktopFilterChange = (filter) => (event) => {
    if (!(filters[filter] === '' && event.target.value === 'all')) {
      handleFilterCommon();
    }

    handleChangeFilters(filter, event.target.value);
  };

  const handleMobileFilterChange = (filter) => (key) => {
    if (!(filters[filter] === '' && key === 'all')) {
      handleFilterCommon();
    }
    handleChangeFilters(filter, key);
  };

  const handleResetFilters = useCallback(() => {
    setFilters(initFilter);
    setSearchValue('');
    handleFilterCommon();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoadMore = useCallback(() => {
    setIsClickLoadMore(true);
    setOffset(offset + PAGINATION_DEFAULT_LIMIT);
  }, [offset]);

  const renderEmptyType = useMemo(() => {
    const finishedLoading = !loadingTransaction && !loadingPendingSection;

    const isEmptyData = monthTransactions.length === 0 && pendingSection.length === 0;

    const isEmpty = finishedLoading && isEmptyData;

    // The transaction is empty when no transaction return and at least one filter has value
    const isNotFoundTransaction =
      isEmpty && (!!filters[TYPE] || !!filters[MONTH_TRANSACTIONS] || !!searchValue);
    if (isNotFoundTransaction) {
      return RENDER_TRANSACTION_TYPE.NOT_FOUND;
    }
    // The transaction is empty when no transaction return and all filters are empty
    const isEmptyTransaction =
      isEmpty && !searchValue && !filters[TYPE] && !filters[MONTH_TRANSACTIONS];

    if (isEmptyTransaction) {
      return RENDER_TRANSACTION_TYPE.EMPTY;
    }

    return null;
  }, [
    filters,
    searchValue,
    pendingSection,
    monthTransactions,
    loadingTransaction,
    loadingPendingSection
  ]);

  const isDisableButtons =
    isDisableDashboard || renderEmptyType === RENDER_TRANSACTION_TYPE.EMPTY || showSkeleton;

  const listTransaction = useMemo(() => {
    const isShowPendingSections = pendingSection.length > 0;

    switch (renderEmptyType) {
      case RENDER_TRANSACTION_TYPE.EMPTY:
        return <EmptyTransaction handleReset={handleResetFilters} />;
      case RENDER_TRANSACTION_TYPE.NOT_FOUND:
        return <NoResultTransaction handleReset={handleResetFilters} />;
      default:
        return (
          <>
            {isShowPendingSections && (
              <ListPendingSection
                data={pendingSection}
                refetchPayment={refetchPendingSection}
                title="PENDING"
              />
            )}
            <ListTransaction
              handleLoadMore={handleLoadMore}
              hasMore={hasMore}
              isLoading={loadingTransaction}
              monthTransactions={monthTransactions}
            />
          </>
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loadingTransaction, monthTransactions, pendingSection, renderEmptyType]);

  const statementButton = useMemo(
    () => (
      <Box>
        <Button
          disabled={isDisableDashboard}
          disableRipple
          icon={
            <SharedAsset
              alt=""
              src="/icon/micro/statement-fill.svg"
            />
          }
          onClick={() => {
            setIsOpenStatement(true);
          }}
          variant="outlined"
        >
          Statements
        </Button>
      </Box>
    ),
    [isDisableDashboard]
  );

  /**
   * ******************* Check history handler ********************
   */
  const [isClickLoadMoreCheckHistory, setIsClickLoadMoreCheckHistory] = useState(false);
  const [offsetCheckHistory, setOffsetCheckHistory] = useState(0);

  const updateOriginalTransactionsCheckHistory = (prevData, dataCheckHistory) => {
    const checkDepositList = isClickLoadMoreCheckHistory
      ? [...prevData.checkDeposits.checkDeposits, ...dataCheckHistory.checkDeposits.checkDeposits]
      : dataCheckHistory.checkDeposits.checkDeposits;
    setIsClickLoadMoreCheckHistory(false);

    return {
      checkDeposits: {
        checkDeposits: checkDepositList,
        pagination: dataCheckHistory.checkDeposits.pagination
      }
    };
  };

  const {
    loading,
    error: checkDepositError,
    data: originalDataCheckHistory,
    refetch: refetchCheckDeposits
  } = useQuery(GET_LIST_CHECK_DEPOSIT_QUERY, {
    variables: {
      params: {
        identifier: {
          businessUuid: businessEntityUuid,
          unitAccountId
        },
        filter: {
          status: getStatusPayload(filters[STATUS]),
          month:
            filters[MONTH_CHECK_HISTORY] === '' || filters[MONTH_CHECK_HISTORY] === 'all'
              ? undefined
              : getDateRange(filters[MONTH_CHECK_HISTORY])
        },
        pagination: {
          limit: PAGINATION_DEFAULT_LIMIT,
          offset: offsetCheckHistory
        }
      }
    },
    skipCache: true,
    skip: !unitAccountId,
    noThrow: true,
    updateData: updateOriginalTransactionsCheckHistory
  });

  useError(checkDepositError);
  const dataCheckHistory = useMemoObject(originalDataCheckHistory);

  useEffect(() => {
    if (checkDepositError) {
      createCommonErrorToast({ error: checkDepositError });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkDepositError]);

  const checkDeposits = useMemo(
    () => dataCheckHistory?.checkDeposits?.checkDeposits ?? [],
    [dataCheckHistory]
  );

  const hasMoreCheckHistory = useMemo(
    () => !!dataCheckHistory?.checkDeposits?.pagination?.next && checkDeposits.length > 0,
    [dataCheckHistory?.checkDeposits?.pagination?.next, checkDeposits]
  );

  const renderCheckDepositType = useMemo(() => {
    // The transaction is empty when no transaction return and all filters are empty
    const isEmptyPayment =
      checkDeposits.length === 0 && !filters[STATUS] && !filters[MONTH_CHECK_HISTORY];

    // The transaction is empty when no transaction return and at least one filter has value
    const isNotFoundTransaction =
      checkDeposits.length === 0 && (!!filters[STATUS] || !!filters[MONTH_CHECK_HISTORY]);

    let renderType = RENDER_TRANSACTION_TYPE.SHOW_LIST;

    if (isEmptyPayment) {
      renderType = RENDER_TRANSACTION_TYPE.EMPTY;
    }

    if (isNotFoundTransaction) {
      renderType = RENDER_TRANSACTION_TYPE.NOT_FOUND;
    }

    return renderType;
  }, [filters, checkDeposits]);

  const isHideSkeletonCheckHistory = (dataCheckHistory && !loading) || checkDepositError;
  const [showSkeletonCheckHistory, setShowSkeletonCheckHistory] = useSkeleton(
    isHideSkeletonCheckHistory
  );

  const isDisabledFilter =
    renderCheckDepositType === RENDER_TRANSACTION_TYPE.EMPTY ||
    (loading && !isClickLoadMoreCheckHistory) ||
    showSkeletonCheckHistory ||
    isDisableDashboard;

  const handleFilterCommonCheckHistory = useCallback(() => {
    setOffsetCheckHistory(0);
    setShowSkeletonCheckHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResetFiltersCheckHistory = useCallback(() => {
    setFilters(initFilter);
    handleFilterCommonCheckHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoadMoreCheckHistory = useCallback(() => {
    setIsClickLoadMoreCheckHistory(true);
    setOffsetCheckHistory(offsetCheckHistory + PAGINATION_DEFAULT_LIMIT);
  }, [offsetCheckHistory]);

  const handleDesktopFilterChangeCheckHistory = (filter) => (event) => {
    if (!(filters[filter] === '' && event.target.value === 'all')) {
      handleFilterCommonCheckHistory();
    }

    handleChangeFilters(filter, event.target.value);
  };

  const listCheckDeposit = useMemo(() => {
    switch (renderCheckDepositType) {
      case RENDER_TRANSACTION_TYPE.EMPTY:
        return <EmptyPayment description="You do not have any transaction history" />;
      case RENDER_TRANSACTION_TYPE.NOT_FOUND:
        return <NoResultTransaction handleReset={handleResetFiltersCheckHistory} />;
      default:
        return (
          <ListCheckDeposit
            checkDeposits={checkDeposits}
            handleLoadMore={handleLoadMoreCheckHistory}
            hasMore={hasMoreCheckHistory}
            isLoading={loading}
          />
        );
    }
  }, [
    renderCheckDepositType,
    handleResetFiltersCheckHistory,
    checkDeposits,
    handleLoadMoreCheckHistory,
    loading,
    hasMoreCheckHistory
  ]);

  const buttonPressedTracker = useButtonPressedCallback({
    elementName: 'checks_tab_transactions',
    elementText: 'checks',
    appPageName: 'transaction_history_banking'
  });

  const handleTabChange = (event, newValue) => {
    setFilters(initFilter);
    setCurrentTab(newValue);
    if (newValue === CHECKS_TAB) {
      buttonPressedTracker();
    }
  };

  const enableCheckDepositBtn = useMemo(() => checkIsMobile() || isWebView, [isWebView]);

  const handleOpenCheckDepositModal = () => {
    openCheckDepositModal({
      actionsKit: {
        submitCheckDepositSuccessHandler: () => {
          if (offset === 0) {
            // page 1 => just refetch
            refetchCheckDeposits();
          } else {
            setOffset(() => 0);
          } // set offset to page 1 with the same filter
        }
      }
    });
  };

  const handleFinishCheckDepositEvent = useCallback(() => {
    /**
     * If month filter is not changed, we need to refetch data
     */
    if (filters[MONTH_TRANSACTIONS] === initFilter[MONTH_TRANSACTIONS]) {
      refetchPendingSection();
      refetchCheckDeposits();
    }

    handleResetFilters();
  }, [filters, handleResetFilters, refetchCheckDeposits, refetchPendingSection]);

  useEventListeners([
    {
      event: FINISH_CHECK_DEPOSIT,
      eventHandler: handleFinishCheckDepositEvent
    }
  ]);

  return (
    <DialogContentStyled>
      <Stack spacing="24px">
        {!isWebView && (
          <PageTitle
            appPageName="transaction_history_banking"
            title="Transaction History"
            to={FAQ_CHECK_HISTORY_URL}
          />
        )}
        <Stack spacing="16px">
          {currentTab === TRANSACTIONS_TAB ? (
            <FilterTransactions
              filters={filters}
              handleChangeFilters={handleChangeFilters}
              handleDesktopFilterChange={handleDesktopFilterChange}
              handleFilterCommon={handleFilterCommon}
              isDisableButtons={isDisableButtons}
              searchValue={searchValue}
              setSearchValue={setSearchValue}
              setShowMonthOptions={setShowMonthOptions}
              setShowTypeOptions={setShowTypeOptions}
              statementButton={statementButton}
            />
          ) : (
            <FilterCheckHistory
              enableCheckDepositBtn={enableCheckDepositBtn}
              filters={filters}
              handleDesktopFilterChange={handleDesktopFilterChangeCheckHistory}
              handleOpenCheckDepositModal={handleOpenCheckDepositModal}
              isDisableButtons={isDisabledFilter}
              setShowMonthCheckHistoryOptions={setShowMonthCheckHistoryOptions}
              setShowStatusOptions={setShowStatusOptions}
              statementButton={statementButton}
            />
          )}
        </Stack>
        {isOpenStatement && <StatementsModal handleClose={() => setIsOpenStatement(false)} />}
        <Stack
          minHeight="400px"
          width="100%"
        >
          <TabContext value={currentTab}>
            <TabBoxStyled>
              <TabList onChange={handleTabChange}>
                <Tab
                  label="Transactions"
                  value={TRANSACTIONS_TAB}
                />
                <Tab
                  label="Checks"
                  value={CHECKS_TAB}
                />
              </TabList>
            </TabBoxStyled>
            <TabPanel
              sx={{ px: 0, paddingTop: 6 }}
              value={TRANSACTIONS_TAB}
            >
              {showSkeleton ? <TransactionSkeleton /> : listTransaction}
            </TabPanel>
            <TabPanel
              sx={{ px: 0, paddingTop: 6 }}
              value={CHECKS_TAB}
            >
              {showSkeletonCheckHistory ? <TransactionSkeleton /> : listCheckDeposit}
            </TabPanel>
          </TabContext>
        </Stack>
        <MobileDrawer
          handleClose={() => setShowMonthOptions(false)}
          open={showMonthOptions}
        >
          <FilterOptions
            callback={() => setShowMonthOptions(false)}
            handleClick={handleMobileFilterChange(MONTH_TRANSACTIONS)}
            options={monthOptions}
            selectedKey={filters[MONTH_TRANSACTIONS]}
          />
        </MobileDrawer>
        <MobileDrawer
          handleClose={() => setShowTypeOptions(false)}
          open={showTypeOptions}
        >
          <FilterOptions
            callback={() => setShowTypeOptions(false)}
            handleClick={handleMobileFilterChange(TYPE)}
            options={typeOptions}
            selectedKey={filters[TYPE]}
          />
        </MobileDrawer>
        <MobileDrawer
          handleClose={() => setShowMonthCheckHistoryOptions(false)}
          open={showMonthCheckHistoryOptions}
        >
          <FilterOptions
            callback={() => setShowMonthCheckHistoryOptions(false)}
            handleClick={handleMobileFilterChange(MONTH_CHECK_HISTORY)}
            options={monthOptions}
            selectedKey={filters[MONTH_CHECK_HISTORY]}
          />
        </MobileDrawer>
        <MobileDrawer
          handleClose={() => setShowStatusOptions(false)}
          open={showStatusOptions}
        >
          <FilterOptions
            callback={() => setShowStatusOptions(false)}
            handleClick={handleMobileFilterChange(STATUS)}
            options={checkDepositStatusOptions}
            selectedKey={filters[STATUS]}
          />
        </MobileDrawer>
      </Stack>
    </DialogContentStyled>
  );
}
